import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api, useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  FileSpreadsheet, 
  Users, 
  Loader2, 
  Check, 
  AlertTriangle,
  ArrowRight
} from 'lucide-react';

const JoinTeamPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, login } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState(null);
  
  // Signup form state
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      verifyInvitation();
    } else {
      setError('Invalid invitation link');
      setLoading(false);
    }
  }, [token, verifyInvitation]);

  const verifyInvitation = useCallback(async () => {
    try {
      const response = await api.post('/api/team/join', { token });
      setInviteData(response.data);
      
      if (response.data.action === 'login') {
        // Existing user - redirect to login or dashboard
        if (user) {
          toast.success('You have joined the team!');
          navigate('/dashboard');
        } else {
          toast.success('You have joined the team! Please login.');
          navigate('/login');
        }
      } else if (response.data.action === 'signup') {
        // New user needs to create account
        setEmail(response.data.email);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  }, [token, user, navigate]);

  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setJoining(true);
    try {
      const response = await api.post('/api/team/join/signup', {
        email,
        password,
        name,
        invitation_id: inviteData.invitation_id
      });
      
      // Store token and update auth state
      localStorage.setItem('token', response.data.access_token);
      
      toast.success('Welcome to the team!');
      window.location.href = '/dashboard'; // Full reload to refresh auth state
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-slate-600">Verifying invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Invalid Invitation</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Link to="/">
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
              Go to Homepage
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Show signup form for new users
  if (inviteData?.action === 'signup') {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <FileSpreadsheet className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-heading font-bold text-slate-900">CRM Data Cleaner</span>
            </Link>
          </div>
        </header>

        <main className="flex items-center justify-center p-4 py-12">
          <div className="max-w-md w-full">
            {/* Welcome Banner */}
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-2xl p-6 text-white mb-6 text-center">
              <Users className="w-10 h-10 mx-auto mb-3 text-indigo-200" />
              <h1 className="text-xl font-bold mb-2">You&apos;re Invited!</h1>
              <p className="text-indigo-100 text-sm">
                Create your account to join the Enterprise team and start cleaning data together.
              </p>
            </div>

            {/* Signup Form */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Create Your Account</h2>
              
              <form onSubmit={handleSignup} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <Input
                    type="email"
                    value={email}
                    disabled
                    className="bg-slate-50"
                  />
                  <p className="text-xs text-slate-500 mt-1">This email was specified in the invitation</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Your Name</label>
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Doe"
                    required
                    data-testid="join-team-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    required
                    minLength={6}
                    data-testid="join-team-password-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Confirm Password</label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    required
                    data-testid="join-team-confirm-password-input"
                  />
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-12"
                  disabled={joining}
                  data-testid="join-team-submit-btn"
                >
                  {joining ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  {joining ? 'Creating Account...' : 'Join Team & Create Account'}
                </Button>
              </form>

              {/* Benefits */}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <p className="text-xs font-medium text-slate-500 mb-3">What you&apos;ll get:</p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-500" />
                    Enterprise plan access (shared)
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-500" />
                    1M rows/month shared with team
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-500" />
                    All premium cleaning features
                  </li>
                </ul>
              </div>
            </div>

            <p className="text-center text-sm text-slate-500 mt-4">
              Already have an account?{' '}
              <Link to="/login" className="text-indigo-600 hover:text-indigo-700 font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </main>
      </div>
    );
  }

  // Success state (should auto-redirect, but show fallback)
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
        <Check className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-slate-900 mb-2">You&apos;ve Joined the Team!</h1>
        <p className="text-slate-600 mb-6">Welcome aboard! You now have access to the shared Enterprise plan.</p>
        <Link to="/dashboard">
          <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
            Go to Dashboard
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default JoinTeamPage;
